import React, { useState, useEffect, useCallback } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { useSelector } from 'react-redux';
import {
  Box, Card, CardContent, Typography, Chip, CircularProgress, Alert,
  Snackbar, Fade, Button, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, useMediaQuery, MenuItem,
  FormControl, InputLabel, Paper, Select
} from '@mui/material';
import {
  Refresh, ReportProblem, Add, NavigateBefore,
  NavigateNext, ImageOutlined, Visibility
} from '@mui/icons-material';
import {
  getKeluhanList, getKeluhanById, createKeluhan, createKeluhanFollowUp
} from '../API/keluhan';
import { getBantuan } from '../API/bantuan';
import { getBantuanLangkahByBantuan } from '../API/bantuanLangkah';

const imgUrl = (filename) => filename ? `${process.env.REACT_APP_URL}/files/images/${filename}` : null;

const Keluhan = () => {
  const userRole = useSelector((s) => s.authAdmin?.user?.role);
  const userId = useSelector((s) => s.authAdmin?.user?.id);
  const isAdmin = userRole === 'admin';
  const isTfl = userRole === 'TFL';
  const isAgr = userRole === 'AGR';
  const canCreate = isAdmin || isTfl || isAgr;
  const canFollowUp = isAdmin || isTfl;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchBantuan, setSearchBantuan] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ 
    id: null, 
    BantuanLangkahId: '', 
    keluhan: '', 
    tindakLanjut: '', 
    bukti: null 
  });
  const [buktiPreview, setBuktiPreview] = useState(null);
  const [langkahList, setLangkahList] = useState([]);
  const [loadingLangkah, setLoadingLangkah] = useState(false);
  const [bantuanList, setBantuanList] = useState([]);
  const [loadingBantuan, setLoadingBantuan] = useState(false);
  const [selectedBantuanId, setSelectedBantuanId] = useState('');
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUp, setFollowUp] = useState({ id: null, catatan: '', status: 'Proses', foto: null });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const isMobile = useMediaQuery('(max-width:800px)');
  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ============================================================
  // FETCH DATA KELUHAN
  // ============================================================
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (searchBantuan) params.BantuanId = searchBantuan;
      const res = await getKeluhanList(params);
      setRows(res.data.data || []);
      setTotalItems(res.data.totalItems ?? 0);
      setTotalPages(res.data.totalPages ?? 1);
    } catch (err) {
      console.error(err);
      notify('Gagal mengambil data keluhan', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchBantuan]);

  useEffect(() => {
    const t = setTimeout(() => fetchList(), 400);
    return () => clearTimeout(t);
  }, [fetchList]);

  // ============================================================
  // FETCH BANTUAN LANGKAH UNTUK DROPDOWN
  // ============================================================
  const fetchLangkahList = async (bantuanId) => {
    if (!bantuanId) {
      setLangkahList([]);
      return;
    }
    setLoadingLangkah(true);
    try {
      const res = await getBantuanLangkahByBantuan(bantuanId);
      setLangkahList(res.data.data || []);
    } catch (err) {
      console.error('Error fetching langkah:', err);
      notify('Gagal mengambil data tahapan bantuan', 'error');
    } finally {
      setLoadingLangkah(false);
    }
  };

  // ============================================================
  // FETCH BANTUAN LIST UNTUK DROPDOWN (role-aware via backend)
  // Admin: semua bantuan; TFL/AGR: backend akan filter; penerima: filter client-side
  // ============================================================
  const fetchBantuanList = async () => {
    setLoadingBantuan(true);
    try {
      // ambil banyak data untuk dropdown
      const res = await getBantuan({ page: 1, limit: 1000 });
      let list = res.data.data || [];

      // Jika bukan admin/TFL/AGR, dianggap penerima -> filter hanya bantuan yang punya pemohon matching userId
      if (!isAdmin && !isTfl && !isAgr) {
        list = list.filter((b) => {
          const pemohon = b.pemohon || {};
          // bandingkan beberapa kemungkinan identifier
          if (pemohon.id && userId && pemohon.id === userId) return true;
          if (pemohon.noKtp && userId && String(pemohon.noKtp) === String(userId)) return true;
          if (b.noWaPenerimaBantuan && pemohon?.NoWa && userId && String(pemohon.id) === String(userId)) return true;
          return false;
        });
      }

      setBantuanList(list);
    } catch (err) {
      console.error('Error fetching bantuan list:', err);
      notify('Gagal mengambil daftar bantuan', 'error');
    } finally {
      setLoadingBantuan(false);
    }
  };

  // ============================================================
  // OPEN ADD
  // ============================================================
  const openAdd = () => {
    setFormMode('add');
    setForm({ 
      id: null, 
      BantuanLangkahId: '', 
      keluhan: '', 
      tindakLanjut: '', 
      bukti: null 
    });
    setBuktiPreview(null);
    setLangkahList([]);
    setBantuanList([]);
    setFormOpen(true);
    fetchBantuanList();
  };

  // ============================================================
  // HANDLE FILE UPLOAD
  // ============================================================
  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setForm((p) => ({ ...p, bukti: f }));
      setBuktiPreview(URL.createObjectURL(f));
    }
  };

  // ============================================================
  // HANDLE SAVE
  // ============================================================
  const handleSave = async () => {
    if (!form.BantuanLangkahId) return notify('Tahapan Bantuan wajib dipilih', 'warning');
    if (!form.keluhan.trim()) return notify('Isi keluhan wajib diisi', 'warning');
    
    const fd = new FormData();
    fd.append('BantuanLangkahId', form.BantuanLangkahId);
    fd.append('keluhan', form.keluhan.trim());
    if (form.bukti) fd.append('bukti', form.bukti);

    setSaving(true);
    try {
      await createKeluhan(fd);
      notify('Keluhan berhasil ditambahkan');
      setFormOpen(false);
      fetchList();
    } catch (err) {
      console.error(err);
      notify(err.response?.data?.message || 'Gagal menyimpan keluhan', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // HANDLE DELETE
  // ============================================================
  const saveFollowUp = async () => {
    if (!followUp.catatan.trim() || !followUp.foto) return notify('Catatan dan foto tindak lanjut wajib diisi', 'warning');
    const fd = new FormData();
    fd.append('catatan', followUp.catatan.trim());
    fd.append('status', followUp.status);
    fd.append('foto', followUp.foto);
    setSaving(true);
    try {
      await createKeluhanFollowUp(followUp.id, fd);
      setFollowUpOpen(false);
      notify('Tindak lanjut berhasil dicatat');
      fetchList();
    } catch (err) {
      notify(err.response?.data?.message || 'Gagal menyimpan tindak lanjut', 'error');
    } finally { setSaving(false); }
  };

  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const response = await getKeluhanById(row.id);
      setDetail(response.data.data);
    } catch (err) {
      notify(err.response?.data?.message || 'Gagal mengambil detail keluhan', 'error');
      setDetailOpen(false);
    } finally { setDetailLoading(false); }
  };

  // ============================================================
  // HANDLE BANTUAN ID CHANGE (untuk fetch langkah)
  // ============================================================
  const handleBantuanIdChange = (e) => {
    const value = e.target.value;
    setSearchBantuan(value);
    setPage(1);
  };

  // ============================================================
  // RENDER
  // ============================================================
  const contentStyle = isMobile 
    ? {} 
    : { marginLeft: "250px", marginRight: "20px" };

  return (
    <div className="table-responsive rounded border">
      <div className="main-content" style={contentStyle}>
        <Box sx={{ mt: 1, mb: 2, width: "100%", maxWidth: "100%" }}>
          {/* HEADER */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReportProblem color="error" />
              <Typography variant="h5" fontWeight={700}>Data Keluhan</Typography>
              <Chip 
                size="small" 
                label={userRole || 'USER'} 
                color={isAdmin ? 'primary' : isTfl ? 'success' : isAgr ? 'warning' : 'default'} 
              />
            </Box>
            <Stack direction="row" spacing={1}>
              {canCreate && (
                <Button variant="contained" color="success" startIcon={<Add />} size="small" onClick={openAdd}>
                  Tambah
                </Button>
              )}
              <Button variant="outlined" startIcon={<Refresh />} size="small" onClick={fetchList}>
                Muat Ulang
              </Button>
            </Stack>
          </Box>

          {/* CARD */}
          <Card elevation={2}>
            <CardContent>
              {/* FILTER */}
              <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <TextField
                  type="number"
                  label="Filter Bantuan ID"
                  variant="outlined"
                  size="small"
                  value={searchBantuan}
                  onChange={handleBantuanIdChange}
                  sx={{ minWidth: 200 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {isAdmin ? 'Semua data' : isTfl ? 'Data dampingan TFL' : isAgr ? 'Data dampingan AGR' : ''}
                </Typography>
              </Stack>

              {/* TABLE */}
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead style={{ backgroundColor: '#f5f7fa' }}>
                    <tr>
                      {['No.', 'Keluhan', 'Status', 'Tindak Lanjut', 'Bukti', 'Bantuan', 'Tahapan', 'Pengirim', 'Tanggal', 'Aksi'].map((h) => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={9} style={{ padding: '32px', textAlign: 'center' }}><CircularProgress size={26} /></td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Tidak ada keluhan.</td></tr>
                    ) : rows.map((row, i) => (
                      <tr key={row.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px' }}>{(page - 1) * limit + i + 1}</td>
                        <td style={{ padding: '10px', maxWidth: 250 }}>{row.keluhan}</td>
                        <td style={{ padding: '10px' }}><Chip size="small" label={row.status} color={row.status === 'Selesai' ? 'success' : row.status === 'Proses' ? 'info' : 'warning'} /></td>
                        <td style={{ padding: '10px', maxWidth: 200 }}>
                          {row.Logs?.some((log) => log.eventType !== 'DIBUAT') ? row.Logs.filter((log) => log.eventType !== 'DIBUAT').map((log) => (
                            <Box key={log.id} sx={{ mb: 1 }}><Typography variant="body2">{log.catatan}</Typography>{log.foto && <Button size="small" onClick={() => openInAppFilePreview({ url: imgUrl(log.foto), title: "Foto tindak lanjut" })}>Lihat foto</Button>}<Typography variant="caption" display="block">{log.Actor?.username} - {new Date(log.createdAt).toLocaleString('id-ID')}</Typography></Box>
                          )) : <Chip size="small" color="warning" label="Belum ditindaklanjuti" />}
                        </td>
                        <td style={{ padding: '10px' }}>
                          {row.bukti
                            ? <Button size="small" onClick={() => openInAppFilePreview({ url: imgUrl(row.bukti), title: "Bukti keluhan" })}>
                                <img src={imgUrl(row.bukti)} alt="bukti" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }} />
                              </Button>
                            : <ImageOutlined color="disabled" />}
                        </td>
                        <td style={{ padding: '10px' }}>
                          {row.BantuanLangkahLog?.BantuanId ? (
                            <Chip size="small" label={`Bantuan #${row.BantuanLangkahLog.BantuanId}`} />
                          ) : '-'}
                        </td>
                        <td style={{ padding: '10px' }}>
                          {row.BantuanLangkahLog?.tahap || '-'}
                        </td>
                        <td style={{ padding: '10px' }}>{row.Admin?.username || '-'}</td>
                        <td style={{ padding: '10px' }}>{new Date(row.createdAt).toLocaleDateString('id-ID')}</td>
                        <td style={{ padding: '10px' }}>
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => openDetail(row)}>Detail</Button>
                            {canFollowUp && <Button size="small" variant="contained" onClick={() => { setFollowUp({ id: row.id, catatan: '', status: 'Proses', foto: null }); setFollowUpOpen(true); }}>Tindak Lanjut</Button>}
                          </Stack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {/* PAGINATION */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box component="select" value={limit}
                    onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    sx={{ px: 1, py: 0.8, border: '1px solid #ccc', borderRadius: 1, fontSize: 14 }}>
                    {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">Hal. {page} / {totalPages} ({totalItems} data)</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" size="small" startIcon={<NavigateBefore />} disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}>{!isMobile && 'Prev'}</Button>
                  <Button variant="outlined" size="small" endIcon={<NavigateNext />} disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>{!isMobile && 'Next'}</Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>

          {/* ============================================================ */}
          {/* MODAL FORM TAMBAH / EDIT KELUHAN */}
          {/* ============================================================ */}
          <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReportProblem color="error" /> 
              {formMode === 'add' ? 'Tambah Keluhan' : 'Edit Keluhan'}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {/* Pilih Bantuan terlebih dahulu */}
                <FormControl fullWidth>
                  <InputLabel>Bantuan</InputLabel>
                  <Select
                    value={selectedBantuanId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedBantuanId(val);
                      // reset langkah dan form field
                      setForm((p) => ({ ...p, BantuanLangkahId: '' }));
                      if (val) fetchLangkahList(val);
                      else setLangkahList([]);
                    }}
                    label="Bantuan"
                    size="small"
                  >
                    <MenuItem value="">Pilih Bantuan</MenuItem>
                    {loadingBantuan ? (
                      <MenuItem value="">Memuat...</MenuItem>
                    ) : (
                      bantuanList.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.pemohon?.nama || b.namaLengkap || `Bantuan #${b.id}`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* BantuanLangkah ID - Dropdown */}
                <FormControl fullWidth required>
                  <InputLabel>Tahapan Bantuan</InputLabel>
                  <Select
                    value={form.BantuanLangkahId}
                    onChange={(e) => setForm((p) => ({ ...p, BantuanLangkahId: e.target.value }))}
                    label="Tahapan Bantuan"
                    disabled={formMode === 'edit'}
                  >
                    <MenuItem value="">Pilih Tahapan Bantuan</MenuItem>
                    {langkahList.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        Bantuan #{item.BantuanId} - {item.tahap} ({item.status})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Input Bantuan ID untuk mencari tahapan */}
                {/* <TextField
                  label="Cari Bantuan ID"
                  type="number"
                  fullWidth
                  value={searchBantuan}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearchBantuan(val);
                    if (val) fetchLangkahList(val);
                    else setLangkahList([]);
                  }}
                  helperText="Masukkan ID Bantuan untuk melihat daftar tahapannya"
                /> */}

                {loadingLangkah && <CircularProgress size={24} />}

                {/* Isi Keluhan */}
                <TextField 
                  label="Isi Keluhan" 
                  fullWidth 
                  required 
                  multiline 
                  rows={3}
                  value={form.keluhan}
                  onChange={(e) => setForm((p) => ({ ...p, keluhan: e.target.value }))} 
                />

                {/* Upload Bukti */}
                <Button variant="outlined" component="label" startIcon={<ImageOutlined />}>
                  Upload Bukti (opsional)
                  <input type="file" accept="image/*" hidden onChange={handleFile} />
                </Button>
                
                {buktiPreview && (
                  <Box>
                    <img src={buktiPreview} alt="preview" style={{ 
                      width: 120, height: 120, objectFit: 'cover', 
                      borderRadius: 8, border: '1px solid #e0e0e0' 
                    }} />
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setFormOpen(false)}>Batal</Button>
              <Button variant="contained" color="error" disabled={saving} onClick={handleSave}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Detail Keluhan</DialogTitle>
            <DialogContent dividers>
              {detailLoading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box> : detail && <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box><Typography variant="caption" color="text.secondary">Pelapor</Typography><Typography fontWeight={700}>{detail.Admin?.username || '-'}</Typography><Typography variant="body2">{detail.Admin?.role || '-'}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Status</Typography><Box><Chip label={detail.status} color={detail.status === 'Selesai' ? 'success' : detail.status === 'Proses' ? 'info' : 'warning'} /></Box></Box>
                  <Box><Typography variant="caption" color="text.secondary">Tanggal Laporan</Typography><Typography>{new Date(detail.createdAt).toLocaleString('id-ID')}</Typography></Box>
                </Stack>
                <Box><Typography variant="caption" color="text.secondary">Bantuan dan Tahapan</Typography><Typography fontWeight={700}>Bantuan #{detail.BantuanLangkahLog?.Bantuan?.id || '-'} - {detail.BantuanLangkahLog?.Bantuan?.JenisBantuan || '-'}</Typography><Typography>{detail.BantuanLangkahLog?.tahap || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Isi Keluhan</Typography><Paper variant="outlined" sx={{ p: 2, mt: .5 }}><Typography>{detail.keluhan}</Typography></Paper></Box>
                <Box><Typography variant="caption" color="text.secondary">Foto Bukti Keluhan</Typography><Box mt={1}>{detail.bukti ? <Box component="button" onClick={() => openInAppFilePreview({ url: imgUrl(detail.bukti), title: "Bukti keluhan" })} style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer" }}><img src={imgUrl(detail.bukti)} alt="Bukti keluhan" style={{ width: 180, height: 140, objectFit: "cover", borderRadius: 8 }} /></Box> : <Typography variant="body2">Tidak ada foto bukti.</Typography>}</Box></Box>
                <Box><Typography variant="h6" fontWeight={700}>Riwayat Penanganan</Typography>{detail.Logs?.length ? detail.Logs.map((log) => <Paper key={log.id} variant="outlined" sx={{ p: 2, mt: 1.5 }}><Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}><Box><Typography fontWeight={700}>{log.eventType === 'DIBUAT' ? 'Keluhan Dibuat' : log.eventType === 'DISELESAIKAN' ? 'Keluhan Diselesaikan' : 'Tindak Lanjut'}</Typography><Typography variant="body2">{log.catatan || '-'}</Typography><Typography variant="caption" color="text.secondary">{log.Actor?.username || '-'} ({log.Actor?.role || '-'}) - {new Date(log.createdAt).toLocaleString('id-ID')}</Typography></Box>{log.toStatus && <Chip size="small" label={log.toStatus} />}</Stack>{log.foto && <Box mt={1}><Box component="button" onClick={() => openInAppFilePreview({ url: imgUrl(log.foto), title: "Foto tindak lanjut" })} style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer" }}><img src={imgUrl(log.foto)} alt="Foto tindak lanjut" style={{ width: 160, height: 120, objectFit: "cover", borderRadius: 8 }} /></Box></Box>}</Paper>) : <Typography variant="body2" color="text.secondary" mt={1}>Belum ada riwayat penanganan.</Typography>}</Box>
              </Stack>}
            </DialogContent>
            <DialogActions><Button onClick={() => setDetailOpen(false)}>Tutup</Button></DialogActions>
          </Dialog>

          <Dialog open={followUpOpen} onClose={() => setFollowUpOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Tindak Lanjut Keluhan</DialogTitle>
            <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
              <TextField required multiline rows={4} label="Catatan tindak lanjut" value={followUp.catatan} onChange={(e) => setFollowUp((p) => ({ ...p, catatan: e.target.value }))} />
              <TextField select label="Status" value={followUp.status} onChange={(e) => setFollowUp((p) => ({ ...p, status: e.target.value }))}><MenuItem value="Proses">Proses</MenuItem><MenuItem value="Selesai">Selesai</MenuItem></TextField>
              <Button component="label" variant="outlined" startIcon={<ImageOutlined />}>Foto tindak lanjut wajib<input hidden type="file" accept="image/*" onChange={(e) => setFollowUp((p) => ({ ...p, foto: e.target.files?.[0] || null }))} /></Button>
              {followUp.foto && <Typography variant="caption">{followUp.foto.name}</Typography>}
            </Stack></DialogContent>
            <DialogActions><Button onClick={() => setFollowUpOpen(false)}>Batal</Button><Button variant="contained" disabled={saving} onClick={saveFollowUp}>Simpan</Button></DialogActions>
          </Dialog>

          {/* SNACKBAR */}
          <Snackbar open={snackbar.open} autoHideDuration={4000}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            TransitionComponent={Fade} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
              severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </div>
  );
};

export default Keluhan;
